/**
 * CourseFlow — pure helpers that turn a course's level list into the two
 * things the completion card needs (Player ceremony — Iteration 2):
 *
 *   • the FORWARD control ("Next: <lesson>", "Start Level N", "Back to the
 *     course", or "Finish" only when nothing follows) — resolveNext();
 *   • SCOPED completion stats (`scope: "lesson" | "level" | "course"`) that
 *     sum real per-lesson results across the level / course — scopedStats().
 *
 * Plus the small local-first LessonResultStore the stats read from. It is
 * keyed by course/lesson and shaped so a server store can replace it later
 * (same record shape, same get/put surface). No DOM, no network — every
 * function here is node-testable and is exercised by
 * scripts/check_celebration_ceremony.mjs.
 *
 * Course structure = the public shape of GET /api/learn/course/<id>:
 *   { id, title, levels: [{ id, title, lessons: [{ id, title, free }] }] }
 */

export { FirstAttemptLedger, FIRST_VERDICT, normalizeFirstVerdict } from "./FirstAttemptLedger.js";

export const RESULT_STORE_KEY = "legendlabs.learnResults";
export const COMPLETION_BONUS_XP = 20;
const LEARNER_PARTITIONS_KEY = "__learners";

/** Locate a lesson inside the course's level list. */
export function locateLesson(course, lessonId) {
  const levels = (course && Array.isArray(course.levels)) ? course.levels : [];
  for (let li = 0; li < levels.length; li++) {
    const lessons = Array.isArray(levels[li].lessons) ? levels[li].lessons : [];
    for (let i = 0; i < lessons.length; i++) {
      if (lessons[i] && String(lessons[i].id) === String(lessonId)) {
        return { levelIndex: li, lessonIndex: i, level: levels[li], lesson: lessons[i], levels };
      }
    }
  }
  return null;
}

export function lessonHref(courseId, lessonId) {
  return "/learn/course/" + encodeURIComponent(courseId) +
    "/lesson/" + encodeURIComponent(lessonId);
}

export function courseHref(courseId) {
  return "/learn/course/" + encodeURIComponent(courseId);
}

/**
 * What follows the current lesson.
 *   next lesson in the same level        → { kind:"lesson", label:"Next: <title>" }
 *   first lesson of the next level       → { kind:"level",  label:"Start Level N" }
 *   lesson is the course's last lesson   → { kind:"course", label:"Back to the course" }
 *   no course structure / lesson unknown → { kind:"none",   label:"Finish ✓" }
 * `href` is where the click goes. A premium-locked target (free:false) is
 * never a dead end: the control keeps its label but lands on the course page
 * (which carries the paywall) unless `unlocked` says the learner may enter.
 */
export function resolveNext(course, lessonId, opts = {}) {
  const courseId = (course && course.id) || opts.courseId || "";
  const none = { kind: "none", label: "Finish \u2713", href: "", locked: false, lessonId: "" };
  const loc = locateLesson(course, lessonId);
  if (!loc || !courseId) return none;
  const { levelIndex, lessonIndex, level, levels } = loc;
  const gate = (target) => {
    const locked = !opts.unlocked && target && target.free === false;
    return locked ? courseHref(courseId) : lessonHref(courseId, target.id);
  };
  const sameLevel = Array.isArray(level.lessons) ? level.lessons : [];
  const nextInLevel = sameLevel[lessonIndex + 1];
  if (nextInLevel) {
    const locked = !opts.unlocked && nextInLevel.free === false;
    return {
      kind: "lesson",
      label: "Next: " + String(nextInLevel.title || nextInLevel.id),
      href: gate(nextInLevel), locked, lessonId: String(nextInLevel.id),
    };
  }
  for (let li = levelIndex + 1; li < levels.length; li++) {
    const lessons = Array.isArray(levels[li].lessons) ? levels[li].lessons : [];
    if (!lessons.length) continue;
    const first = lessons[0];
    const locked = !opts.unlocked && first.free === false;
    return {
      kind: "level",
      label: "Start Level " + (li + 1),
      href: gate(first), locked, lessonId: String(first.id),
      levelTitle: String(levels[li].title || ""),
    };
  }
  return { kind: "course", label: "Back to the course", href: courseHref(courseId), locked: false, lessonId: "" };
}

// ── LessonResultStore (local-first; server-replaceable) ────────────────────
// Record shape (one per course/lesson, last completion wins):
//   { xp: number, correct: number, total: number, completed: true, at: epochMs }
// `correct/total` are the skill-check tallies of that play (Iteration 3 will
// widen them to real attempts); `xp` is the session's reward-derived total.

export function resultKey(courseId, lessonId) {
  return String(courseId || "") + "/" + String(lessonId || "");
}

export class LessonResultStore {
  /**
   * `learner` may be a stable uid string or `{ learnerId }`. Omitting it keeps
   * the legacy, unpartitioned namespace readable and writable. Supplying it
   * isolates all reads/writes below `__learners[learnerId]`.
   */
  constructor(storage, learner = null) {
    this._storage = storage || null;
    this._mem = null;
    this._learnerId = typeof learner === "string"
      ? learner
      : (learner && (learner.learnerId || learner.learnerKey || learner.uid));
    this._learnerId = this._learnerId === null || this._learnerId === undefined ||
      String(this._learnerId) === "" ? null : String(this._learnerId);
  }
  _read(refresh = false) {
    if (this._mem && (!refresh || !this._storage)) return this._mem;
    let obj = {};
    try {
      const raw = this._storage && this._storage.getItem(RESULT_STORE_KEY);
      if (raw) obj = JSON.parse(raw) || {};
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) obj = {};
    } catch (_) { obj = {}; }
    this._mem = obj;
    return obj;
  }
  _records(create = false, refresh = false) {
    const all = this._read(refresh);
    if (this._learnerId === null) return all;
    let partitions = all[LEARNER_PARTITIONS_KEY];
    if (!partitions || typeof partitions !== "object" || Array.isArray(partitions)) {
      if (!create) return {};
      partitions = {};
      all[LEARNER_PARTITIONS_KEY] = partitions;
    }
    let records = partitions[this._learnerId];
    if (!records || typeof records !== "object" || Array.isArray(records)) {
      if (!create) return {};
      records = {};
      partitions[this._learnerId] = records;
    }
    return records;
  }
  _write() {
    try {
      if (this._storage) this._storage.setItem(RESULT_STORE_KEY, JSON.stringify(this._read()));
      return true;
    } catch (_) {
      return false;
    }
  }
  get(courseId, lessonId) {
    const r = this._records(false, true)[resultKey(courseId, lessonId)];
    return (r && typeof r === "object") ? r : null;
  }
  put(courseId, lessonId, rec) {
    if (!courseId || !lessonId || !rec) return null;
    // Re-read durable storage before merging so two store instances cannot
    // both act on stale claim/completion state.
    const records = this._records(true, true);
    const key = resultKey(courseId, lessonId);
    const prior = records[key];
    const priorBonus = claimedBonusOf(prior);
    const correct = whole(rec.correct);
    const attempts = whole(rec.attempts === undefined ? rec.total : rec.attempts);
    const total = Math.max(correct, attempts);
    const clean = {
      xp: whole(rec.xp),
      correct,
      total,
      attempts: total,
      misses: Math.min(total - correct,
        whole(rec.misses === undefined ? total - correct : rec.misses)),
      unknown: whole(rec.unknown),
      completed: true,
      at: rec.at || Date.now(),
    };
    // A result update (including a replay) must not unclaim or re-add a bonus.
    const suppliedBonus = claimedBonusOf(rec);
    const bonusXp = Math.max(priorBonus, suppliedBonus);
    if (bonusXp > 0) {
      clean.completionBonusClaimed = true;
      clean.completionBonusXp = bonusXp;
    }
    if (prior && prior.firstCompletedAt) clean.firstCompletedAt = prior.firstCompletedAt;
    else clean.firstCompletedAt = rec.firstCompletedAt || clean.at;
    if (prior && prior.completionBonusEligible === true) {
      clean.completionBonusEligible = true;
    }
    clean.completions = whole(prior && prior.completions) + 1;
    records[key] = clean;
    this._write();
    return clean;
  }
  /**
   * Persist a completion and report whether it was the learner's first.
   * Bonus claiming is intentionally a separate transaction.
   */
  complete(courseId, lessonId, rec) {
    const firstCompletion = !(this.get(courseId, lessonId) || {}).completed;
    const record = this.put(courseId, lessonId, rec);
    if (firstCompletion && record) {
      record.completionBonusEligible = true;
      this._write();
    }
    // Spread the record too, so callers may replace an existing `put(...)`
    // with `complete(...)` without changing their result-field reads.
    return { ...record, record, firstCompletion, first_completion: firstCompletion };
  }
  /**
   * Claim the one-time completion bonus. Repeated calls return claimed:false
   * and never mutate XP. `record.xp` remains board XP; scopedStats adds the
   * separately stored bonus exactly once.
   */
  claimCompletionBonus(courseId, lessonId, amount = COMPLETION_BONUS_XP) {
    const records = this._records(true, true);
    const key = resultKey(courseId, lessonId);
    const record = records[key];
    // Old flat records predate first-completion snapshots. They remain valid
    // results, but are deliberately ineligible for a retroactive award.
    if (!record || !record.completed || record.completionBonusEligible !== true) {
      return { claimed: false, amount: 0, record: record || null };
    }
    const prior = claimedBonusOf(record);
    if (record.completionBonusClaimed || prior > 0) {
      return { claimed: false, amount: 0, record };
    }
    const award = whole(amount);
    if (!award) return { claimed: false, amount: 0, record };
    // A one-time currency award must be durable. Other result APIs remain
    // usable as an in-memory fallback, but a missing store cannot mint XP.
    if (!this._storage) {
      return { claimed: false, amount: 0, record, error: "persistence_unavailable" };
    }
    const previous = {
      eligible: record.completionBonusEligible,
      claimed: record.completionBonusClaimed,
      xp: record.completionBonusXp,
      at: record.completionBonusClaimedAt,
    };
    record.completionBonusClaimed = true;
    record.completionBonusEligible = false;
    record.completionBonusXp = award;
    record.completionBonusClaimedAt = Date.now();
    if (!this._write()) {
      restoreOwn(record, "completionBonusEligible", previous.eligible);
      restoreOwn(record, "completionBonusClaimed", previous.claimed);
      restoreOwn(record, "completionBonusXp", previous.xp);
      restoreOwn(record, "completionBonusClaimedAt", previous.at);
      return { claimed: false, amount: 0, record, error: "persist_failed" };
    }
    return { claimed: true, amount: award, record };
  }
  completionContext(courseId, lessonId) {
    const record = this.get(courseId, lessonId);
    const legacyUnknownTallies = !!record &&
      record.attempts === undefined && record.misses === undefined &&
      record.unknown === undefined && whole(record.total) === 0 &&
      whole(record.correct) === 0;
    return {
      first_completion: !(record && record.completed),
      attempts: legacyUnknownTallies ? null :
        (record ? whole(record.attempts === undefined ? record.total : record.attempts) : 0),
      misses: legacyUnknownTallies ? null : (record ? whole(record.misses === undefined
        ? whole(record.total) - whole(record.correct) : record.misses) : 0),
      unknown: record ? whole(record.unknown) : 0,
      bonus_claimed: !!(record && (record.completionBonusClaimed || claimedBonusOf(record))),
    };
  }
  forLearner(learnerId) { return new LessonResultStore(this._storage, learnerId); }
  all() { return { ...this._records() }; }
  records() { return this.all(); }
}

function whole(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

function claimedBonusOf(record) {
  if (!record || typeof record !== "object") return 0;
  if (record.completionBonusClaimed === false) return 0;
  return whole(record.completionBonusXp === undefined ? record.bonusXp : record.completionBonusXp);
}

function restoreOwn(object, key, value) {
  if (value === undefined) delete object[key];
  else object[key] = value;
}

function isLegacyUnknownResult(record) {
  return !!record && record.completed === true &&
    record.attempts === undefined && record.misses === undefined &&
    record.unknown === undefined && whole(record.correct) === 0 &&
    whole(record.total) === 0;
}

/** Unknown/ungraded results have no accuracy; they are never presented as 100%. */
export function resultAccuracy(correct, attempts, unknown = 0) {
  const c = whole(correct);
  const n = whole(attempts);
  // Partial evidence must not masquerade as a perfect score. Until every
  // expected board has a first verdict, accuracy is deliberately unknown.
  if (whole(unknown) > 0) return null;
  return n > 0 ? Math.round((Math.min(c, n) / n) * 100) : null;
}

/**
 * Scoped completion stats. `live` is the CURRENT play's session record
 * ({xp, correct, total}) — it counts even before it is persisted, and it
 * replaces any older stored record for the same lesson.
 *   scope "lesson" → { xp, acc, done:1, total:1 } from the live session only
 *   scope "level"  → totals across the lesson's level (+ level bonus if the
 *                    structure declares one: level.bonusXp)
 *   scope "course" → totals across every level
 * `acc` is a 0–100 integer, or null when every result is unknown/ungraded.
 * Unknowns are summed and surfaced separately; they are not counted as misses.
 */
export function scopedStats(scope, course, lessonId, store, live) {
  const s = scope === "level" || scope === "course" ? scope : "lesson";
  const cur = {
    xp: whole(live && live.xp),
    correct: whole(live && live.correct),
    total: whole(live && (live.attempts === undefined ? live.total : live.attempts)),
    unknown: whole(live && live.unknown),
    completionBonusXp: claimedBonusOf(live),
  };
  if (s === "lesson") {
    return { scope: s, xp: cur.xp + cur.completionBonusXp,
      acc: resultAccuracy(cur.correct, cur.total, cur.unknown), done: 1, total: 1,
      bonus: cur.completionBonusXp, unknown: cur.unknown };
  }
  const loc = locateLesson(course, lessonId);
  if (!loc) {
    // No structure → degrade to the lesson numbers (never a blank card).
    return { scope: s, xp: cur.xp + cur.completionBonusXp,
      acc: resultAccuracy(cur.correct, cur.total, cur.unknown), done: 1, total: 1,
      bonus: cur.completionBonusXp, unknown: cur.unknown, degraded: true };
  }
  const storedCurrent = store ? store.get((course && course.id) || "", lessonId) : null;
  // Aggregate scopes describe the learner's durable level/course total, so a
  // replay keeps the lesson's old one-time bonus there. Lesson scope above is
  // strictly this run and only displays a bonus supplied by this run.
  cur.completionBonusXp = Math.max(cur.completionBonusXp, claimedBonusOf(storedCurrent));
  const levels = s === "level" ? [loc.level] : loc.levels;
  const courseId = (course && course.id) || "";
  let xp = 0, correct = 0, totalSc = 0, unknown = 0, done = 0, total = 0, bonus = 0;
  for (const lvl of levels) {
    const lessons = Array.isArray(lvl.lessons) ? lvl.lessons : [];
    let lvlDone = 0;
    for (const l of lessons) {
      total++;
      const isCur = String(l.id) === String(lessonId);
      const rec = isCur ? cur : (store ? store.get(courseId, l.id) : null);
      if (!rec || (!isCur && !rec.completed)) continue;
      done++; lvlDone++;
      xp += whole(rec.xp);
      const recBonus = claimedBonusOf(rec);
      xp += recBonus;
      bonus += recBonus;
      correct += whole(rec.correct);
      totalSc += whole(rec.attempts === undefined ? rec.total : rec.attempts);
      unknown += whole(rec.unknown);
      if (isLegacyUnknownResult(rec)) unknown++;
    }
    // A level bonus (if the structure ever declares one) counts once the
    // whole level is done — "level XP with its bonus".
    const b = whole(lvl.bonusXp);
    if (b > 0 && lessons.length && lvlDone === lessons.length) bonus += b;
  }
  // Level bonuses have not yet been included in xp; claimed lesson bonuses have.
  const levelBonus = levels.reduce((sum, lvl) => {
    const lessons = Array.isArray(lvl.lessons) ? lvl.lessons : [];
    let lvlDone = 0;
    for (const l of lessons) {
      const isCur = String(l.id) === String(lessonId);
      const rec = isCur ? cur : (store ? store.get(courseId, l.id) : null);
      if (rec && (isCur || rec.completed)) lvlDone++;
    }
    return sum + (lessons.length && lvlDone === lessons.length ? whole(lvl.bonusXp) : 0);
  }, 0);
  return { scope: s, xp: xp + levelBonus, acc: resultAccuracy(correct, totalSc, unknown),
    done, total, bonus, unknown };
}
